<template>
  <section class="hero hero-bg">
    <div class="hero__container">

      <div class="hero__model-pill">
        <i class="fa-solid fa-bolt"></i>
        <span>基于 {{ state.selectedModelLabel }} · 支持图生视频 &amp; 文生视频</span>
      </div>

      <h1 class="hero__title" style="line-height: 1.2;">和我聊聊，你想要什么视频~</h1>

      <div class="input-card hero__card">
        <!-- 提示词输入区 -->
        <div class="hero__prompt-toolbar">
          <div class="hero__prompt-col">
            <div class="prompt-wrapper hero__prompt-wrapper">
              <div v-if="false" class="prompt-ghost" v-html="state.ghostHtml"></div>
              <textarea ref="promptInputRef"
                        :value="state.promptText"
                        @input="handleInput"
                        @keydown="handleKeydown"
                        rows="3"
                        class="prompt-input-el hero__textarea"
                        placeholder="和我说说，你想要什么画面的视频..."></textarea>
              <div class="hero__enhance-row">
                <button @click="onEnhanceClick" class="ai-enhance-btn" :disabled="state.isEnhancing || !state.promptText.trim()" title="AI 智能扩写提示词">
                  <i :class="state.isEnhancing ? 'fa-solid fa-spinner ai-enhance-spin hero__icon-blue' : 'fa-solid fa-wand-magic-sparkles hero__icon-blue'"></i>
                  {{ state.isEnhancing ? '优化中...' : 'AI 优化' }}
                </button>
              </div>
            </div>
            <!-- 已上传素材缩略图 -->
            <div v-show="hasUploadedMedia" class="hero__images-row">
              <div v-for="img in state.uploadedImages" :key="img.id" class="image-thumbnail">
                <img :src="img.data" :alt="img.name" />
                <span class="remove-btn" @click="removeUploadedImage(img.id)" title="移除图片">✕</span>
              </div>
              <div v-for="vid in state.uploadedVideos" :key="vid.id" class="media-thumbnail media-thumbnail--video">
                <video :src="vid.data" muted preload="metadata"></video>
                <span class="media-thumbnail__badge"><i class="fa-solid fa-film"></i></span>
                <span class="remove-btn" @click="removeUploadedVideo(vid.id)" title="移除视频">✕</span>
              </div>
              <div v-for="aud in state.uploadedAudios" :key="aud.id" class="media-thumbnail media-thumbnail--audio" :title="aud.name">
                <i class="fa-solid fa-music media-thumbnail__audio-icon"></i>
                <span class="media-thumbnail__name">{{ aud.name }}</span>
                <span class="remove-btn" @click="removeUploadedAudio(aud.id)" title="移除音频">✕</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 操作与参数栏 -->
        <div class="hero__actions-row">
          <div class="hero__params">

            <!-- 素材上传按钮 -->
            <button v-if="canAddMedia" @click="onAddMediaClick" class="param-btn hero__upload-btn" data-tip="图片最多9张；视频最多2个(50MB/15s)；音频最多3个(.wav/.mp3)">
              <i class="fa-solid fa-plus hero__upload-icon"></i>添加素材
            </button>

            <!-- @ 引用按钮 -->
            <div v-show="hasUploadedMedia" class="hero__dropdown-wrap">
              <button @mousedown.prevent @click="toggleMentionDrop" class="param-btn">
                <i class="fa-solid fa-at hero__btn-icon-gap"></i>引用素材
              </button>
              <div class="dropdown-panel hero__dropdown-panel hero__dropdown-panel--mention" :class="{ show: state.mentionDropOpen }">
                <template v-if="state.uploadedImages.length > 0">
                  <p class="hero__mention-heading">图片</p>
                  <div class="hero__mention-list">
                    <div v-for="(img, idx) in state.uploadedImages" :key="img.id" class="mention-option" @mousedown.prevent @click="doInsertMention('image', idx)">
                      <img :src="img.data" :alt="'图像' + (idx + 1)" />
                      <span class="mention-name">图像{{ idx + 1 }}</span>
                    </div>
                  </div>
                </template>
                <template v-if="state.uploadedVideos.length > 0">
                  <p class="hero__mention-heading">视频</p>
                  <div class="hero__mention-list">
                    <div v-for="(vid, idx) in state.uploadedVideos" :key="vid.id" class="mention-option" @mousedown.prevent @click="doInsertMention('video', idx)">
                      <i class="fa-solid fa-video mention-option__icon"></i>
                      <span class="mention-name">视频{{ idx + 1 }}</span>
                    </div>
                  </div>
                </template>
                <template v-if="state.uploadedAudios.length > 0">
                  <p class="hero__mention-heading">音频</p>
                  <div class="hero__mention-list">
                    <div v-for="(aud, idx) in state.uploadedAudios" :key="aud.id" class="mention-option" @mousedown.prevent @click="doInsertMention('audio', idx)">
                      <i class="fa-solid fa-music mention-option__icon"></i>
                      <span class="mention-name">音频{{ idx + 1 }}</span>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- 比例分辨率下拉 -->
            <div class="hero__dropdown-wrap">
              <button @click="onRatioDropClick" class="param-btn">
                <i class="fa-solid fa-expand hero__btn-icon-gap"></i>
                <span>{{ state.selectedRatio }}</span>
                <span class="hero__sep">|</span>
                <span>{{ state.selectedRes }}</span>
              </button>
              <div class="dropdown-panel hero__dropdown-panel hero__dropdown-panel--ratio" :class="{ show: state.openDropdown === 'ratio' }">
                <p class="hero__dropdown-label hero__dropdown-label--spaced">选择比例</p>
                <div class="hero__ratio-grid-inner">
                  <div v-for="r in ratios" :key="r.value"
                       class="ratio-item hero__ratio-item"
                       :class="{ active: state.selectedRatio === r.value }"
                       @click="updateRatio(r.value)">
                    <div class="ratio-box hero__ratio-box-gap" :style="{ width: r.boxW + 'px', height: r.boxH + 'px' }"></div>
                    <span class="hero__ratio-label">{{ r.value }}</span>
                  </div>
                </div>
                <p class="hero__dropdown-label">选择分辨率</p>
                <div class="hero__res-row">
                  <button @click="updateRes('720P')"
                          class="hero__res-btn hero__res-btn--left"
                          :class="{ 'hero__res-btn--active': state.selectedRes === '720P' }">720P</button>
                  <button @click="updateRes('1080P')"
                          class="hero__res-btn hero__res-btn--right"
                          :class="{ 'hero__res-btn--active': state.selectedRes === '1080P' }">
                    1080P <i class="fa-solid fa-sparkles hero__res-sparkle"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- 时长下拉 -->
            <div class="hero__dropdown-wrap">
              <button @click="onTimeDropClick" class="param-btn">
                <i class="fa-regular fa-clock hero__btn-icon-gap"></i>
                <span>{{ state.selectedDuration }}</span>
              </button>
              <div class="dropdown-panel hero__dropdown-panel hero__dropdown-panel--time" :class="{ show: state.openDropdown === 'time' }">
                <p class="hero__time-dropdown-heading">选择视频生成时长</p>
                <div class="hero__time-options-inner">
                  <div v-for="d in durations" :key="d"
                       @click="updateDuration(d)"
                       class="time-option hero__time-option-row"
                       :class="{ active: state.selectedDuration === d }">
                    <span class="hero__duration-label">{{ d }}</span>
                    <i class="fa-solid fa-check check-icon hero__check-blue"></i>
                  </div>
                </div>
              </div>
            </div>

            <!-- AI水印开关 -->
            <label class="watermark-toggle" :class="{ 'watermark-toggle--on': state.watermark }" @click.prevent="onWatermarkClick">
              <input type="checkbox" :checked="state.watermark" />
              <i :class="state.watermark ? 'fa-solid fa-check' : 'fa-solid fa-droplet'" style="font-size: 11px;"></i>
              <span class="watermark-toggle__label">AI 水印</span>
            </label>

          </div>

          <!-- 生成按钮 - 深色胶囊 -->
          <button id="main-gen-btn" @click="onSendClick" :disabled="true" class="sunset-disabled" title="Beta 试用已结束，视频生成功能已暂停">
            <i class="fa-solid fa-paper-plane"></i>
            <span>已暂停</span>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAppStore } from '../composables/useAppStore'
import { ratios, durations } from '../data/prompts'
import { reportClick } from '../util/report'

const {
  state,
  toggleDropdown, updateRatio, updateRes, updateDuration,
  enhancePrompt, startGen,
  removeUploadedImage, removeUploadedVideo, removeUploadedAudio, onPromptInput, onPromptKeydown,
  toggleMentionDrop,
} = useAppStore()

const promptInputRef = ref<HTMLTextAreaElement | null>(null)

const hasUploadedMedia = computed(
  () =>
    state.uploadedImages.length > 0 ||
    state.uploadedVideos.length > 0 ||
    state.uploadedAudios.length > 0,
)

const canAddMedia = computed(
  () =>
    state.uploadedImages.length < 9 ||
    state.uploadedVideos.length < 2 ||
    state.uploadedAudios.length < 3,
)

function doInsertMention(type: 'image' | 'video' | 'audio', idx: number) {
  const labels: Record<string, string> = { image: '图像', video: '视频', audio: '音频' }
  const tag = `@${labels[type]}${idx + 1} `
  const el = promptInputRef.value
  state.mentionDropOpen = false
  if (!el) {
    state.promptText += tag
    return
  }
  el.focus()
  document.execCommand('insertText', false, tag)
  state.promptText = el.value
}

function onSendClick() {
  reportClick('index', 'send')
  startGen()
}

function onWatermarkClick() {
  reportClick('index', 'watermark')
  state.watermark = !state.watermark
}

function onRatioDropClick() {
  reportClick('index', 'video_ratio')
  toggleDropdown('ratio')
}

function onTimeDropClick() {
  reportClick('index', 'video_time')
  toggleDropdown('time')
}

function onEnhanceClick() {
  reportClick('index', 'ai_prompt')
  void enhancePrompt()
}

function onAddMediaClick() {
  reportClick('index', 'add_image')
  triggerUpload()
}

function triggerUpload() {
  const input = document.getElementById('media-upload-input') as HTMLInputElement | null
  input?.click()
}

function handleInput(e: Event) {
  const t = e.target as HTMLTextAreaElement
  onPromptInput(t.value)
}

function handleKeydown(e: KeyboardEvent) {
  const accepted = onPromptKeydown(e)
  if (accepted && promptInputRef.value) {
    promptInputRef.value.value = state.promptText
  }
}
</script>

<style scoped>
.hero {
  padding-bottom: 3rem;
}

.hero__container {
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  padding-top: 6rem;
  padding-bottom: 2rem;
  text-align: center;
  padding-left: 1rem;
  padding-right: 1rem;
  position: relative;
  z-index: 10;
}

.hero__model-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 18px;
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  margin-bottom: 1.25rem;
}

.hero__model-pill i {
  color: #facc15;
  font-size: 12px;
}

.hero__title {
  font-size: 40px;
  font-weight: 800;
  margin-bottom: 2.5rem;
  letter-spacing: -0.025em;
  color: #111827;
}

.hero__card {
  padding: 1.25rem;
  text-align: left;
  position: relative;
  margin-top: 1rem;
}

.hero__prompt-toolbar {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  padding-top: 0.25rem;
}

.hero__prompt-col {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.hero__textarea {
  width: 100%;
  font-size: 16px;
  line-height: 1.625;
  outline: none;
  resize: none;
  padding-top: 0.25rem;
  padding-right: 5.5rem;
  max-height: 130px;
  overflow-y: auto;
  scrollbar-width: none;
}

.hero__textarea::-webkit-scrollbar {
  display: none;
}

.hero__textarea:focus {
  outline: none;
}

.hero__prompt-wrapper {
  position: relative;
}

.hero__enhance-row {
  position: absolute;
  right: 0;
  bottom: 4px;
  z-index: 2;
}

.hero__icon-blue {
  color: #3b82f6;
}

.hero__actions-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.5rem;
}

.hero__params {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.hero__model-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: #6366f1;
  background: #eef2ff;
  border-radius: 999px;
  white-space: nowrap;
  user-select: none;
}

.hero__upload-btn {
  flex-shrink: 0;
  position: relative;
}

.hero__upload-btn:hover .hero__upload-icon {
  color: #4b5563;
}

.hero__upload-btn[data-tip]:hover::after {
  content: attr(data-tip);
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
}

.hero__upload-icon {
  margin-right: 0.5rem;
  color: #9ca3af;
}

.hero__upload-preview-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.hero__thumb-wrap {
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.hero__thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero__badge-added {
  color: #2563eb;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 500;
}

.hero__remove-thumb {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 9999px;
  background-color: #e5e7eb;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 0.125rem;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.hero__remove-thumb:hover {
  background-color: #fee2e2;
  color: #ef4444;
}

.hero__dropdown-wrap {
  position: relative;
}

.hero__btn-icon-gap {
  margin-right: 0.5rem;
}

.hero__sep {
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  color: #d1d5db;
}

.hero__dropdown-panel--ratio {
  border-radius: 1rem;
  padding: 1.25rem;
  width: 320px;
}

.hero__dropdown-panel--time {
  border-radius: 1rem;
  overflow: hidden;
  width: 220px;
}

.hero__dropdown-label {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 0.75rem;
  font-weight: 500;
}

.hero__dropdown-label--spaced {
  margin-bottom: 1rem;
}

.hero__ratio-grid-inner {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.5rem;
  padding: 0.375rem;
  border-radius: 0.75rem;
  margin-bottom: 1.5rem;
  background-color: #f9fafb;
}

.hero__ratio-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  cursor: pointer;
}

.hero__ratio-box-gap {
  margin-bottom: 0.25rem;
}

.hero__ratio-label {
  font-size: 10px;
}

.hero__res-row {
  display: flex;
  padding: 0.25rem;
  border-radius: 0.75rem;
  background-color: #f9fafb;
}

.hero__res-btn {
  flex: 1;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  font-size: 13px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6b7280;
  font-family: inherit;
}

.hero__res-btn:focus {
  outline: none;
}

.hero__res-btn:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.hero__res-btn--left.hero__res-btn--active {
  border-radius: 0.5rem;
  background-color: #ffffff;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  font-weight: 700;
  color: #2563eb;
}

.hero__res-btn--right.hero__res-btn--active {
  border-radius: 0.5rem;
  background-color: #ffffff;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  font-weight: 700;
  color: #2563eb;
}

.hero__res-sparkle {
  color: #60a5fa;
  margin-left: 0.25rem;
}

.hero__time-dropdown-heading {
  padding: 0.75rem 1rem;
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  border-bottom: 1px solid #f3f4f6;
}

.hero__time-options-inner {
  padding: 0.375rem;
}

.hero__time-option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  margin-bottom: 0.25rem;
}

.hero__duration-label {
  font-size: 14px;
}

.hero__check-blue {
  color: #2563eb;
}

.hero__images-row {
  display: flex;
  gap: 0.75rem;
  padding: 12px 0.75rem 4px;
  margin-bottom: 0.25rem;
  overflow-x: auto;
  overflow-y: visible;
}

.hero__dropdown-panel--mention {
  border-radius: 1rem;
  overflow: hidden;
  width: 240px;
}

.hero__mention-heading {
  padding: 0.75rem 1rem;
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  border-bottom: 1px solid #f3f4f6;
}

.hero__mention-list {
  padding: 0.375rem;
}

/* ===== Beta 下线禁用 — REQ-SUNSET-002 ===== */
#main-gen-btn.sunset-disabled {
  background: #f3f4f6 !important;
  color: #d1d5db !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
  transform: none !important;
  pointer-events: none !important;
  border: 1px solid transparent;
}
</style>
